import { Gender } from 'apps/user/types/user.enum.type';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import Spacer from 'components/Spacer';
import { useFormik } from 'formik';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { selectAccountDetails } from 'selectors/account-selector';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as Yup from 'yup';
import UpdatePassword from './UpdatePassword';
import Documentation from './Documentation';
import { getImageExtension } from 'utils';
import { UpdateProfileInfo } from 'apps/user/types/on-boarding.type';
import { useProfileInfoUpdate } from 'common/queries-and-mutations/user';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { fetchUserInformation } from 'thunks/account-thunk';
import ImageAvatar from 'components/ImageAvatar';

export default function Profile() {
  const { user } = useAppSelector(selectAccountDetails);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState<File>();
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const { mutate, isSuccess, isLoading, isError } = useProfileInfoUpdate();
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      surname: user.surname || '',
      othername: user.othername || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      gender: user.gender || '',
      dateOfBirth: user.dateOfBirth || '',
      homeAddress: user.homeAddress || '',
      imageBase64: '',
      base64FileName: ''
    },
    validationSchema: Yup.object({
      surname: Yup.string().required('First name is required').nullable(),
      othername: Yup.string().required('Last name is required').nullable(),
      email: Yup.string().email().required('Email is required').nullable(),
      phoneNumber: Yup.string().required('Phone number is required').nullable(),
      gender: Yup.string().required('Gender is required').nullable(),
      dateOfBirth: Yup.date().required('Date of birth is required').nullable(),
      homeAddress: Yup.string().required('Home address is required').nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      handleSubmit(values);
    }
  });

  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement;
    const file: File = (target.files as FileList)[0];

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      if (reader.result) {
        formik.setFieldValue('imageBase64', reader.result.toString());
        setFile(file);
      }
    };
  };

  const handleSubmit = (updateProfileInfo: UpdateProfileInfo) => {
    let fileName = '';
    if (file) {
      fileName = `${user.id}_${getImageExtension(file.name)}`;
    }
    mutate({ ...updateProfileInfo, base64FileName: fileName });
  };

  const openFileLibrary = () => {
    hiddenFileInputRef.current?.click();
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert type="success" message="Profile Successfully Updated" />);
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (file) {
        location.reload();
      }
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div>
      <div className="col-span-full mt-4 grid h-auto grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg bg-white p-5 xsm:grid-cols-2 md:col-span-2 md:gap-5">
          <p className="text-sm font-medium text-primary-800">
            Account Information
          </p>
          <form
            autoComplete="false"
            className="mt-10"
            onSubmit={formik.handleSubmit}
          >
            <div className="relative h-32 w-32">
              <input
                type="file"
                hidden
                id="file_upload"
                accept=".png, .jpg, .jpeg"
                ref={hiddenFileInputRef}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleImage(event);
                }}
              />
              <ImageAvatar
                imageUrl={formik.values.imageBase64 || user.image}
                className="h-32 w-32 rounded-lg object-cover"
              />
              <div
                className="absolute bottom-0 right-0 p-2"
                role="button"
                onClick={() => {
                  openFileLibrary();
                }}
              >
                <Icon
                  name={Icons.EditProfile}
                  fill={user.image ? '' : '#FF6600'}
                />
              </div>
            </div>
            <div className="my-8">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-4 w-full">
                  <TextField
                    name="surname"
                    placeholder=""
                    value={formik.values.surname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="First Name"
                    error={formik.touched.surname ? formik.errors.surname : ''}
                    isRequired
                    size="lg"
                    className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-4 w-full">
                  <TextField
                    name="othername"
                    placeholder=""
                    value={formik.values.othername}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Last Name"
                    error={
                      formik.touched.othername ? formik.errors.othername : ''
                    }
                    isRequired
                    size="lg"
                    className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-4 w-full">
                  <TextField
                    name="email"
                    placeholder=""
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email"
                    error={formik.touched.email ? formik.errors.email : ''}
                    size="lg"
                    isRequired
                    className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-4 w-full">
                  <TextField
                    name="phoneNumber"
                    placeholder=""
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Phone Number"
                    error={
                      formik.touched.phoneNumber
                        ? formik.errors.phoneNumber
                        : ''
                    }
                    size="lg"
                    isRequired
                    className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-4 w-full">
                  <div className="relative mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <label className="text-[12px] font-medium text-[#353535]">
                        Gender
                      </label>
                      <div className="mx-2 mb-2">
                        <Spacer width={3} />
                        <Icon name={Icons.Required} />
                      </div>
                    </span>
                  </div>
                  <div className="mt-5 flex items-center gap-10">
                    <button
                      type="button"
                      onClick={() =>
                        formik.setFieldValue('gender', Gender.MALE)
                      }
                      className="flex items-center gap-3"
                    >
                      <Icon
                        name={
                          formik.values.gender === Gender.MALE
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                      <span className="text-sm font-normal text-[#353535]">
                        Male
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formik.setFieldValue('gender', Gender.FEMALE)
                      }
                      className="flex items-center gap-3"
                    >
                      <Icon
                        name={
                          formik.values.gender === Gender.FEMALE
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                      <span className="text-sm font-normal text-[#353535]">
                        Female
                      </span>
                    </button>
                  </div>
                </div>
                <div className="group relative z-0 mb-4 w-full">
                  <TextField
                    name="dateOfBirth"
                    type="date"
                    placeholder=""
                    value={formik.values.dateOfBirth}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="D.O.B"
                    error={
                      formik.touched.dateOfBirth
                        ? formik.errors.dateOfBirth
                        : ''
                    }
                    size="lg"
                    isRequired
                    className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-4 w-full">
                  <TextField
                    name="homeAddress"
                    placeholder=""
                    value={formik.values.homeAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Address"
                    error={
                      formik.touched.homeAddress
                        ? formik.errors.homeAddress
                        : ''
                    }
                    size="lg"
                    isRequired
                    className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-end justify-end gap-8">
              <Button
                variant="primary"
                size="lg"
                className="mt-1 h-[2.8rem] w-[9rem] rounded"
                label="Save"
                type="submit"
                disabled={!formik.isValid}
                loading={isSubmitting || isLoading}
              ></Button>
            </div>
          </form>
        </div>
        <div className="mr-14 flex flex-col gap-4">
          <UpdatePassword />
          <Documentation />
        </div>
      </div>
    </div>
  );
}
